/**
 * B2BPricelistsPage — Phase 51
 *
 * Allows B2B Agents (role = 'agent') to create and manage price lists directly
 * from the B2B Portal.  Stores/Customers (role = 'client') never see this page.
 *
 * Key design decisions:
 *  • Reuses the exact same Add/Edit modal UI from PricelistsSettings (ERP module).
 *  • The modal fields are identical: Name, Type (Manual vs Percentage), Discount %.
 *  • On INSERT, `created_by` is set to auth.uid() for full origin traceability.
 *  • `company_id` is taken from `session.agentRecord.company_id`.
 *  • RLS policies (20260423_phase51_fix_agent_pricelist_rls.sql) use the
 *    established get_agent_company_id_for_jwt() SECURITY DEFINER function
 *    (Phase 39) — no auth.users query required.
 *
 * Route: /b2b/pricelists  (accessible only when session.role === 'agent')
 *
 * All UI strings are in Hebrew per the B2B portal standard.
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Tags,
  Plus,
  Pencil,
  Trash2,
  X,
  Loader2,
  AlertCircle,
  Percent,
  SlidersHorizontal,
  Package,
  CheckCircle2,
  ShieldAlert,
} from 'lucide-react';
import { supabase }    from '@/lib/supabase';
import { useB2BUser }  from './B2BUserContext';
import type { Pricelist } from '../inventory/PricelistsSettings';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtPct(n: number) {
  return `${Number(n).toFixed(2).replace(/\.?0+$/, '')}%`;
}

// ─── Main page component ───────────────────────────────────────────────────────

export function B2BPricelistsPage() {
  const { session } = useB2BUser();
  const navigate    = useNavigate();

  // ── RBAC guard — only agents may access this page ─────────────────────────
  useEffect(() => {
    if (!session) return;          // still loading — wait
    if (session.role !== 'agent') {
      navigate('/b2b/catalog', { replace: true });
    }
  }, [session, navigate]);

  // Agent's own company_id (the supplier they represent)
  const companyId = session?.agentRecord?.company_id ?? null;

  // ── Pricelist list state ───────────────────────────────────────────────────
  const [lists,      setLists]      = useState<Pricelist[]>([]);
  const [listsLoad,  setListsLoad]  = useState(true);
  const [listsError, setListsError] = useState<string | null>(null);

  // ── Modal state (Add / Edit) ───────────────────────────────────────────────
  const [plModalOpen, setPlModalOpen] = useState(false);
  const [plEditing,   setPlEditing]   = useState<Pricelist | null>(null);
  const [plName,      setPlName]      = useState('');
  const [plDiscount,  setPlDiscount]  = useState('0');
  const [plIsManual,  setPlIsManual]  = useState(false);
  const [plSaving,    setPlSaving]    = useState(false);
  const [plFormErr,   setPlFormErr]   = useState<string | null>(null);

  // ── Delete confirm state ───────────────────────────────────────────────────
  const [plDelTarget, setPlDelTarget] = useState<Pricelist | null>(null);
  const [plDeleting,  setPlDeleting]  = useState(false);

  // ── Toast state ───────────────────────────────────────────────────────────
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const showToast = useCallback((msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // ── Fetch pricelists ───────────────────────────────────────────────────────
  // NOTE: The Supabase client sends the agent's JWT automatically.
  // RLS policy "ipl: agents can insert own company" (Phase 51 fix) and
  // "inventory_pricelists: b2b agent SELECT" (Phase 39) use
  // get_agent_company_id_for_jwt() to authorize access — no extra headers needed.

  const fetchLists = useCallback(async () => {
    if (!companyId) return;
    setListsLoad(true);
    setListsError(null);
    const { data, error } = await supabase
      .from('inventory_pricelists')
      .select('*')
      .eq('company_id', companyId)
      .order('name');
    if (error) setListsError(error.message);
    else       setLists((data as Pricelist[]) ?? []);
    setListsLoad(false);
  }, [companyId]);

  useEffect(() => { fetchLists(); }, [fetchLists]);

  // ── Modal helpers ──────────────────────────────────────────────────────────

  const openPlAdd = () => {
    setPlEditing(null);
    setPlName('');
    setPlDiscount('0');
    setPlIsManual(false);
    setPlFormErr(null);
    setPlModalOpen(true);
  };

  const openPlEdit = (pl: Pricelist) => {
    setPlEditing(pl);
    setPlName(pl.name);
    setPlDiscount(String(pl.default_discount_percent));
    setPlIsManual(pl.is_manual);
    setPlFormErr(null);
    setPlModalOpen(true);
  };

  const closePlModal = () => {
    setPlModalOpen(false);
    setPlEditing(null);
    setPlName('');
    setPlDiscount('0');
    setPlIsManual(false);
    setPlFormErr(null);
  };

  // ── Validation ─────────────────────────────────────────────────────────────

  const validatePl = (): string | null => {
    if (!plName.trim()) return 'שם המחירון הוא שדה חובה';
    if (!plIsManual) {
      const d = parseFloat(plDiscount);
      if (isNaN(d) || d < 0 || d > 100)
        return 'הנחת ברירת מחדל חייבת להיות בין 0 ל-100';
    }
    return null;
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  // The INSERT payload includes company_id explicitly so the RLS WITH CHECK
  // clause (company_id = get_agent_company_id_for_jwt()) can verify it.
  // If company_id were missing from the payload, PostgREST would not include it
  // in the new row and the RLS check would fail → 403.

  const handlePlSave = async () => {
    const err = validatePl();
    if (err) { setPlFormErr(err); return; }
    if (!companyId) {
      setPlFormErr('פרטי החברה של הסוכן לא נמצאו — התחבר מחדש');
      return;
    }

    setPlSaving(true);
    setPlFormErr(null);

    const payload = {
      name:                     plName.trim(),
      default_discount_percent: plIsManual ? 0 : parseFloat(plDiscount),
      is_manual:                plIsManual,
    };

    try {
      if (plEditing) {
        const { error } = await supabase
          .from('inventory_pricelists')
          .update(payload)
          .eq('id', plEditing.id);
        if (error) throw error;
        showToast('מחירון עודכן בהצלחה');
      } else {
        // Fetch the current user's auth UID for the created_by field
        const { data: { user } } = await supabase.auth.getUser();

        const { error } = await supabase
          .from('inventory_pricelists')
          .insert({
            company_id: companyId,           // ← required for RLS WITH CHECK
            created_by: user?.id ?? null,    // ← traceability: originated from B2B portal
            ...payload,
          });
        if (error) throw error;
        showToast('מחירון נוסף בהצלחה');
      }
      closePlModal();
      fetchLists();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'שגיאה בשמירת המחירון';
      if (msg.toLowerCase().includes('unique'))
        setPlFormErr('מחירון עם שם זה כבר קיים');
      else
        setPlFormErr(msg);
    } finally {
      setPlSaving(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────

  const handlePlDelete = async () => {
    if (!plDelTarget) return;
    setPlDeleting(true);
    const { error } = await supabase
      .from('inventory_pricelists')
      .delete()
      .eq('id', plDelTarget.id);
    if (error) {
      showToast(error.message, false);
    } else {
      showToast('מחירון נמחק');
      setPlDelTarget(null);
      fetchLists();
    }
    setPlDeleting(false);
  };

  // ── Render guards ──────────────────────────────────────────────────────────

  if (!session) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] gap-3 text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin" />
        <span className="text-sm">טוען…</span>
      </div>
    );
  }

  if (session.role !== 'agent') return null;

  if (!companyId) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12" dir="rtl">
        <div className="flex items-start gap-4 p-5 bg-red-50 border border-red-200 rounded-2xl text-red-700">
          <ShieldAlert className="w-6 h-6 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-sm">פרטי החברה של הסוכן לא הוגדרו</p>
            <p className="text-xs mt-1 opacity-80">
              פרופיל הסוכן שלך חסר מזהה חברה. אנא פנה למנהל המערכת לאימות הגדרות הסוכן.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6" dir="rtl">

      {/* ── Toast ──────────────────────────────────────────────────────────── */}
      {toast && (
        <div
          className={[
            'fixed top-20 right-4 left-4 md:left-auto md:w-80 z-[60]',
            'flex items-center gap-3 px-4 py-3 rounded-2xl shadow-xl text-sm font-medium',
            toast.ok ? 'bg-green-600 text-white' : 'bg-red-600 text-white',
          ].join(' ')}
        >
          {toast.ok
            ? <CheckCircle2 className="w-4 h-4 shrink-0" />
            : <AlertCircle  className="w-4 h-4 shrink-0" />
          }
          <span className="flex-1">{toast.msg}</span>
          <button onClick={() => setToast(null)} className="p-0.5 opacity-70 hover:opacity-100">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* ── Page header ────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-violet-100 rounded-xl">
            <Tags className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">מחירונים</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              ניהול מחירוני הנחה ומחירונים פרטניים עבור לקוחותיך
            </p>
          </div>
        </div>

        {/* RBAC: "הוסף מחירון" button — ONLY for agents */}
        {session.role === 'agent' && (
          <button
            onClick={openPlAdd}
            className="flex items-center gap-2 px-4 py-2.5 bg-violet-600 hover:bg-violet-700
                       text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            הוסף מחירון
          </button>
        )}
      </div>

      {/* ── Agent identity banner ───────────────────────────────────────────── */}
      {session.agentRecord && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-orange-50 border border-orange-200
                        rounded-xl text-xs text-orange-700">
          <SlidersHorizontal className="w-3.5 h-3.5 shrink-0" />
          <span>
            יוצר כסוכן: <strong>{session.agentRecord.name}</strong>
            {session.agentRecord.email && (
              <span className="opacity-70"> · {session.agentRecord.email}</span>
            )}
          </span>
        </div>
      )}

      {/* ── Error ──────────────────────────────────────────────────────────── */}
      {listsError && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200
                        rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {listsError}
        </div>
      )}

      {/* ── Price list table ────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

        {/* Table header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/60">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            שם המחירון
          </p>
          <div className="flex gap-6">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">סוג</p>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">הנחת בסיס</p>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide w-20 text-left">פעולות</p>
          </div>
        </div>

        {/* Loading */}
        {listsLoad ? (
          <div className="flex items-center justify-center gap-3 py-16 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">טוען מחירונים…</span>
          </div>
        ) : lists.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-4 text-gray-400">
            <Tags className="w-12 h-12 text-gray-200" />
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600">אין מחירונים עדיין</p>
              <p className="text-xs text-gray-400 mt-1">
                לחץ על "הוסף מחירון" כדי ליצור את הראשון
              </p>
            </div>
            {session.role === 'agent' && (
              <button
                onClick={openPlAdd}
                className="flex items-center gap-2 px-4 py-2 bg-violet-50 hover:bg-violet-100
                           text-violet-700 text-sm font-medium rounded-xl transition-colors
                           border border-violet-200"
              >
                <Plus className="w-4 h-4" />
                הוסף מחירון
              </button>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-gray-50">
            {lists.map(pl => (
              <li
                key={pl.id}
                className="flex items-center justify-between px-6 py-4 hover:bg-gray-50/60
                           transition-colors"
              >
                {/* Name + type badge */}
                <div className="flex items-center gap-3 min-w-0">
                  <div className={[
                    'p-1.5 rounded-lg shrink-0',
                    pl.is_manual ? 'bg-blue-50' : 'bg-violet-50',
                  ].join(' ')}>
                    {pl.is_manual
                      ? <SlidersHorizontal className="w-3.5 h-3.5 text-blue-500" />
                      : <Percent          className="w-3.5 h-3.5 text-violet-500" />
                    }
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{pl.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {pl.is_manual
                        ? 'מחיר פרטני לכל מוצר'
                        : `הנחת בסיס: ${fmtPct(pl.default_discount_percent)}`
                      }
                    </p>
                  </div>
                </div>

                {/* Right side: type badge + discount + actions */}
                <div className="flex items-center gap-6 shrink-0">

                  {/* Type badge */}
                  {pl.is_manual ? (
                    <span className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5
                                     bg-blue-50 text-blue-600 text-[10px] font-bold rounded
                                     border border-blue-200 shrink-0">
                      <Package className="w-2.5 h-2.5" />
                      ידני
                    </span>
                  ) : (
                    <span className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5
                                     bg-violet-50 text-violet-600 text-[10px] font-bold rounded
                                     border border-violet-200 shrink-0">
                      <Percent className="w-2.5 h-2.5" />
                      הנחה
                    </span>
                  )}

                  {/* Default discount */}
                  <span className="hidden sm:block text-sm font-semibold text-gray-700 w-20 text-center">
                    {pl.is_manual ? '—' : fmtPct(pl.default_discount_percent)}
                  </span>

                  {/* Actions */}
                  <div className="flex items-center gap-1 w-20 justify-end">
                    <button
                      onClick={() => openPlEdit(pl)}
                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50
                                 rounded-lg transition-colors"
                      title="ערוך מחירון"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setPlDelTarget(pl)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50
                                 rounded-lg transition-colors"
                      title="מחק מחירון"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Footer count */}
        {lists.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-50 text-xs text-gray-400 bg-gray-50/40">
            {lists.length} מחירונים בקטלוג החברה שלך
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          הוספת / עריכת מחירון — Same modal fields as ERP PricelistsSettings
          ══════════════════════════════════════════════════════════════════════ */}
      {plModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 space-y-5">

            {/* Modal title */}
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">
                {plEditing ? 'עריכת מחירון' : 'הוספת מחירון חדש'}
              </h3>
              <button
                onClick={closePlModal}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Inline error */}
            {plFormErr && (
              <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200
                              rounded-lg text-sm text-red-700">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {plFormErr}
              </div>
            )}

            {/* ── שדה 1: שם המחירון ── */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">
                שם המחירון <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={plName}
                onChange={e => setPlName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handlePlSave()}
                placeholder="לדוגמה: לקוחות VIP, סיטונאי, קמעונאי…"
                autoFocus
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                           focus:outline-none focus:ring-2 focus:ring-violet-400
                           focus:border-transparent"
              />
            </div>

            {/* ── שדה 2: סוג המחירון ── */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">סוג המחירון</label>
              <div className="grid grid-cols-2 gap-2">

                {/* אחוזים */}
                <button
                  type="button"
                  onClick={() => setPlIsManual(false)}
                  className={[
                    'flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border-2',
                    'text-sm font-medium transition-all',
                    !plIsManual
                      ? 'border-violet-500 bg-violet-50 text-violet-700'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50',
                  ].join(' ')}
                >
                  <Percent className={`w-5 h-5 ${!plIsManual ? 'text-violet-600' : 'text-gray-400'}`} />
                  <span className="text-xs leading-tight text-center">
                    הנחה קבועה<br />(אחוזים)
                  </span>
                </button>

                {/* מחירון ידני */}
                <button
                  type="button"
                  onClick={() => setPlIsManual(true)}
                  className={[
                    'flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border-2',
                    'text-sm font-medium transition-all',
                    plIsManual
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50',
                  ].join(' ')}
                >
                  <SlidersHorizontal className={`w-5 h-5 ${plIsManual ? 'text-blue-600' : 'text-gray-400'}`} />
                  <span className="text-xs leading-tight text-center">
                    מחירון פרטני<br />(ידני)
                  </span>
                </button>
              </div>

              {/* Type description */}
              {plIsManual ? (
                <p className="text-xs text-blue-600 bg-blue-50 px-3 py-2 rounded-lg border border-blue-100">
                  מחיר ספציפי לכל מוצר — מוגדר מתוך טופס המוצר.
                  אם לא הוגדר מחיר, יחול מחיר הבסיס של המוצר.
                </p>
              ) : (
                <p className="text-xs text-gray-400">
                  אחוז הנחה יחול על מחיר הבסיס של כל מוצר. ניתן להגדיר חריגים לפי קבוצה או מוצר ספציפי.
                </p>
              )}
            </div>

            {/* ── שדה 3: הנחת ברירת מחדל (רק עבור מחירון אחוזים) ── */}
            {!plIsManual && (
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  הנחת ברירת מחדל (%)
                </label>
                <div className="relative">
                  <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4
                                      text-gray-400 pointer-events-none" />
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={plDiscount}
                    onChange={e => setPlDiscount(e.target.value)}
                    dir="ltr"
                    className="w-full pr-3 pl-9 py-2.5 border border-gray-300 rounded-lg text-sm
                               focus:outline-none focus:ring-2 focus:ring-violet-400
                               focus:border-transparent"
                  />
                </div>
                <p className="text-xs text-gray-400">
                  הנחה זו תחול על כל מוצר שאין לו כלל ספציפי במחירון
                </p>
              </div>
            )}

            {/* Modal actions */}
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={closePlModal}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800
                           border border-gray-200 rounded-lg transition-colors"
              >
                ביטול
              </button>
              <button
                onClick={handlePlSave}
                disabled={plSaving || !plName.trim()}
                className="flex items-center gap-2 px-5 py-2 bg-violet-600 hover:bg-violet-700
                           disabled:opacity-50 disabled:cursor-not-allowed
                           text-white text-sm font-medium rounded-lg transition-colors"
              >
                {plSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                {plEditing ? 'שמור שינויים' : 'הוסף מחירון'}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          אישור מחיקה
          ══════════════════════════════════════════════════════════════════════ */}
      {plDelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 space-y-5">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-xl shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">מחיקת מחירון</h3>
                <p className="text-sm text-gray-500 mt-1">
                  האם למחוק את המחירון{' '}
                  <span className="font-semibold text-gray-800">"{plDelTarget.name}"</span>?
                  {plDelTarget.is_manual
                    ? ' כל מחירי המוצרים שהוגדרו במחירון זה יימחקו גם כן.'
                    : ' כל הכללים הספציפיים שלו יימחקו גם כן.'
                  }
                  {' '}פעולה זו אינה ניתנת לביטול.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => setPlDelTarget(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800
                           border border-gray-200 rounded-lg transition-colors"
              >
                ביטול
              </button>
              <button
                onClick={handlePlDelete}
                disabled={plDeleting}
                className="flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700
                           disabled:opacity-50 text-white text-sm font-medium rounded-lg
                           transition-colors"
              >
                {plDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
                מחק
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
